// Onboarding service for hotel onboarding APIs

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend.sortmyspot.com/';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

// Get all onboardings for a specific user
export const getUserOnboardings = async (userId) => {
  console.log('Fetching onboardings for userId:', userId);
  try {
    const response = await fetch(`${API_BASE_URL}/web/onboarding/user/${userId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const data = await response.json();
    console.log('User Onboardings Data:', data);
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch user onboardings');
    }

    return data;
  } catch (error) {
    console.error('Get user onboardings error:', error);
    throw error;
  }
};

// Create new onboarding
export const createOnboarding = async (onboardingData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/web/onboarding/add`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(onboardingData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to create onboarding');
    }

    return data;
  } catch (error) {
    console.error('Create onboarding error:', error);
    throw error;
  }
};

// Get single onboarding by ID
export const getOnboarding = async (onboardingId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/web/onboarding/${onboardingId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch onboarding');
    }

    return data;
  } catch (error) {
    console.error('Get onboarding error:', error);
    throw error;
  }
};

// List onboardings with filters
export const listOnboardings = async (filters = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/web/onboarding/list`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        q: filters.query || {},
        p: filters.project || {},
        s: filters.sort || { createdAt: -1 },
        o: filters.skip || 0,
        l: filters.limit || 10,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch onboardings');
    }

    return data;
  } catch (error) {
    console.error('List onboardings error:', error);
    throw error;
  }
};

// Update onboarding
export const updateOnboarding = async (onboardingId, onboardingData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/web/onboarding/${onboardingId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(onboardingData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update onboarding');
    }

    return data;
  } catch (error) {
    console.error('Update onboarding error:', error);
    throw error;
  }
};

// Delete onboarding
export const deleteOnboarding = async (onboardingId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/web/onboarding/${onboardingId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete onboarding');
    }

    return data;
  } catch (error) {
    console.error('Delete onboarding error:', error);
    throw error;
  }
};

// Find onboardings
export const findOnboardings = async (searchData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/web/onboarding/find`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(searchData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to find onboardings');
    }

    return data;
  } catch (error) {
    console.error('Find onboardings error:', error);
    throw error;
  }
};

// Aggregate onboardings
export const aggregateOnboardings = async (pipeline) => {
  try {
    const response = await fetch(`${API_BASE_URL}/web/onboarding/aggregate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(pipeline),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to aggregate onboardings');
    }

    return data;
  } catch (error) {
    console.error('Aggregate onboardings error:', error);
    throw error;
  }
};

// Upload images to gallery (for hotel images that go to gallery)
export const uploadGalleryImages = async (files) => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('gallery', file);
    });

    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/web/onboarding/upload/gallery`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to upload gallery images');
    }

    return data;
  } catch (error) {
    console.error('Upload gallery images error:', error);
    throw error;
  }
};

// Upload images to slider (for hotel main images that go to slider)
export const uploadSliderImages = async (files) => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('slider', file);
    });

    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/web/onboarding/upload/slider`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to upload slider images');
    }

    return data;
  } catch (error) {
    console.error('Upload slider images error:', error);
    throw error;
  }
};

// Upload owner documents to owner_details folder in S3
export const uploadOwnerDocuments = async (files) => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('ownerDocs', file);
    });

    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/web/onboarding/upload/owner-details`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to upload owner documents');
    }

    return data;
  } catch (error) {
    console.error('Upload owner documents error:', error);
    throw error;
  }
};

// Remove image from S3
export const removeImage = async (filePath) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/web/onboarding/removeImage`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify({ filePath }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to remove image');
    }

    return data;
  } catch (error) {
    console.error('Remove image error:', error);
    throw error;
  }
};
